<template>
  <v2-modal
    :show="show"
    :title="modalTitle"
    size="md"
    @close="onClose"
  >
    <!-- Meeting Form -->
    <template v-if="!isSuccess">
      <div class="v2-schedule-meeting__subtitle">
        We will send your meeting request to {{ brandDetails.entityName }} with details you included this form.
      </div>
      <div class="v2-schedule-meeting__form">
        <div class="v2-schedule-meeting__label">
          <span>Meeting Objective</span>
          <span class="v2-schedule-meeting__required">*</span>
        </div>
        <textarea
          v-model="meetingObjective"
          class="v2-schedule-meeting__textarea"
          placeholder="Type in your meeting objective"
          rows="5"
        />
        <div
          v-if="showError"
          class="v2-schedule-meeting__error"
        >
          Meeting Objective must be at least 10 characters
        </div>
      </div>
      <div class="v2-schedule-meeting__actions">
        <v2-button
          variant="outline"
          @click="onClose"
        >
          Cancel
        </v2-button>
        <v2-button
          :disabled="!isValid || isScheduling"
          :loading="isScheduling"
          @click="scheduleMeeting"
        >
          {{ isScheduling ? 'Scheduling...' : 'Schedule a meeting' }}
        </v2-button>
      </div>
    </template>

    <!-- Success State -->
    <template v-else>
      <div class="v2-schedule-meeting__success">
        <div class="v2-schedule-meeting__success-icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--v2-success)"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <div class="v2-schedule-meeting__success-title">
          The brand has been notified.
        </div>
        <div class="v2-schedule-meeting__success-message">
          The brand has been notified, and we will get back to you shortly.
        </div>
        <div class="v2-schedule-meeting__actions">
          <v2-button @click="onClose">
            Done
          </v2-button>
        </div>
      </div>
    </template>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import { apiToastWarning } from '@/@core/utils/toast'
import store from '@/store'
import { SCHEDULE_MEETING } from '@/store/modules/notification.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'

export default {
  name: 'V2ScheduleMeetingModal',
  components: { V2Modal, V2Button },
  props: {
    show: { type: Boolean, default: false },
    brandDetails: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      meetingObjective: '',
      isScheduling: false,
      isSuccess: false,
      hasBlurred: false,
    }
  },
  computed: {
    isValid() {
      return this.meetingObjective.trim().length >= 10
    },
    showError() {
      return this.hasBlurred && !this.isValid && this.meetingObjective.length > 0
    },
    modalTitle() {
      return `Schedule a Meeting with ${this.brandDetails.entityName || ''}`
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.meetingObjective = ''
        this.isSuccess = false
        this.hasBlurred = false
        analytics.track(constants.TRACKS.ACTIONS.RETAILER_CLICKS_BOOK_A_MEETING)
      }
    },
    meetingObjective() {
      if (this.meetingObjective.length > 0) {
        this.hasBlurred = true
      }
    },
  },
  methods: {
    scheduleMeeting() {
      if (!this.isValid) return
      const payload = {
        meetingObjective: this.meetingObjective,
        brandId: this.$route?.params?.brandId || '',
      }
      this.isScheduling = true
      store
        .dispatch(SCHEDULE_MEETING, payload)
        .then(() => {
          this.isScheduling = false
          this.isSuccess = true
        })
        .catch(err => {
          this.isScheduling = false
          apiToastWarning(err)
        })
    },
    onClose() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-schedule-meeting {
  &__subtitle {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-6);
  }

  &__form {
    margin-bottom: var(--v2-space-4);
  }

  &__label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-2);
  }

  &__required {
    color: var(--v2-destructive);
    margin-left: var(--v2-space-1);
  }

  &__textarea {
    width: 100%;
    padding: var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-background);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    resize: vertical;
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: var(--v2-primary);
    }

    &::placeholder {
      color: var(--v2-muted-foreground);
    }
  }

  &__error {
    font-size: 0.75rem;
    color: var(--v2-destructive);
    margin-top: var(--v2-space-1);
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-3);
    margin-top: var(--v2-space-6);
  }

  &__success {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  &__success-icon {
    margin-bottom: var(--v2-space-3);
  }

  &__success-title {
    font-family: var(--v2-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-2);
  }

  &__success-message {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-4);
  }
}
</style>
